import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { CircleHelp, Coins, Compass, Landmark, LogIn, LogOut, MapPin, Menu, MessageCircle, Newspaper, ShoppingBag, Swords, Trophy } from "lucide-react";
import { SoundDeck } from "@/audio/SoundDeck";
import { Face } from "@/city3d/Hud";
import { Logo, Mark } from "@/city3d/Logo";
import { PANEL, Pill, Pop, Reel, Ring, Skel } from "./kit";
import { THEMES, dur, fmt, useMedia, useNow, useTheme } from "./util";

const STOPS = [["blue", "Docks"], ["green", "Uptown"], ["rose", "Mansion"], ["gold", "Mayor"]];

// A round white button. Counts sit on it in red; `quiet` badges (a rank, a countdown) in ink. `dot` means something new.
const Tray = ({ icon: Icon, label, badge, quiet, dot, testId, className = "", ...p }) => (
  <button type="button" aria-label={label} title={label} data-testid={testId} {...p}
    className={`${PANEL} lift pointer-events-auto relative grid h-9 w-9 place-items-center rounded-full sm:h-11 sm:w-11 ${className}`}>
    <Icon className="h-[18px] w-[18px]" strokeWidth={2.2} />
    {badge ? <span key={badge} className={`badge-pop absolute -right-1.5 -top-1.5 whitespace-nowrap rounded-full px-1.5 text-center text-[11px] font-semibold leading-[18px] tabular-nums ring-2 ring-[var(--x-solid)] ${quiet ? "bg-x-ink text-x-surface" : "bg-x-neg text-white"}`}>{badge}</span> : null}
    {dot && <span key="dot" className="badge-pop absolute right-0 top-0 h-3 w-3 rounded-full bg-x-pos ring-2 ring-white" />}
  </button>
);

const SWATCH = { auto: "linear-gradient(135deg, #f5f6f8 50%, #1c1e24 50%)", light: "#f5f6f8", dark: "#1c1e24", clear: "rgba(255,255,255,.25)", tinted: "#cfe8e4" }; // taste-skill-ignore: a hard split, Auto is half light and half dark

function ThemePicker() {
  const [theme, setTheme] = useTheme();
  return (
    <div className="px-3 pb-1 pt-2">
      <div className="mb-1.5 text-xs font-medium text-x-mute">Theme</div>
      <div className="flex justify-between" role="radiogroup" aria-label="Theme">
        {THEMES.map(([id, label]) => (
          <button key={id} type="button" role="radio" aria-checked={theme === id} onClick={() => setTheme(id)} data-testid={`theme-${id}`}
            className="flex flex-col items-center gap-1 text-[11px] font-medium">
            <span className={`h-8 w-8 rounded-full border border-x-line ${theme === id ? "outline outline-2 outline-offset-2 outline-x-accent" : ""}`} style={{ background: SWATCH[id] }} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

const MenuItem = ({ icon: Icon, children, className = "", ...p }) => (
  <button type="button" {...p} className={`flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-x-raised ${className}`}>
    <Icon className="h-4 w-4 text-x-mute" strokeWidth={2.2} />{children}
  </button>
);

export function TopBar({ user, me, econ, rank, clockOffset, stop, unread, newPaper, panel, guide, onGuide, onFly, onOpen, onChat, onLedger, onTour, onSignIn, onSignOut }) {
  const now = useNow(clockOffset);
  const [menu, setMenu] = useState(null);
  const calm = useReducedMotion();
  const phone = useMedia("(max-width: 639px)");
  const pulse = useRef();
  useEffect(() => {
    if (!calm) pulse.current?.animate([{ transform: "scale(1)" }, { transform: "scale(1.06)" }, { transform: "scale(1)" }], { duration: 300, delay: 500 });
  }, [econ?.uxd, calm]);
  const go = (fn) => () => { setMenu(null); fn(); };
  const toggle = (id) => () => setMenu(menu === id ? null : id);
  const prog = !econ ? 0 : stop === 0 ? econ.uxd / econ.line : econ.week.row ? econ.week.earned / econ.week.row : econ.week.earned > 0 ? 1 : 0;
  const [acc, label] = STOPS[stop];
  const closes = econ?.hall.closes_at && dur(Date.parse(econ.hall.closes_at) - now);
  const badge = <Pill acc={acc} className="whitespace-nowrap !text-[11px]">{stop === 2 ? `${label} #${me?.mansion?.rank}` : label}</Pill>;
  const signOut = <MenuItem icon={LogOut} onClick={go(onSignOut)} data-testid="menu-signout">Sign out</MenuItem>;

  return (
    <header className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-start gap-2 px-2 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:px-4" data-testid="topbar"
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}>
      <div className="relative flex shrink-0 items-center gap-3 sm:justify-self-start">
        {econ && user ? (
          <>
            <span className="hidden 2xl:block"><Mark size={34} /></span>
            <button type="button" onClick={toggle("me")} data-pop data-testid="topbar-me" title="Your account"
              className={`${PANEL} lift pointer-events-auto relative flex items-center gap-2 rounded-full p-1 sm:pr-4`}>
              <Ring value={prog} size={46} stroke={3} acc={acc}><Face p={{ a: user.avatar_url, h: user.h }} size={36} ring="transparent" /></Ring>
              <span className="hidden text-left leading-tight sm:block">
                <span className="block text-sm font-semibold">@{user.h}</span>
                {badge}
              </span>
              <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 rounded-full bg-white sm:hidden">{badge}</span>
            </button>
            {menu === "me" && (
              <Pop onClose={() => setMenu(null)} className="absolute left-0 top-full mt-3 w-56">
                <MenuItem icon={MapPin} onClick={go(onFly)} data-testid="menu-fly">Fly home</MenuItem>
                <MenuItem icon={CircleHelp} onClick={go(onTour)}>Replay the tour</MenuItem>
                {signOut}
              </Pop>
            )}
          </>
        ) : user ? (
          <span className={`${PANEL} rounded-full p-1`}><Face p={{ a: user.avatar_url, h: user.h }} size={36} ring="transparent" /></span>
        ) : <Logo size={30} />}
      </div>

      <div className="flex min-w-0 flex-1 justify-center sm:flex-none">
        {econ ? (
          <button type="button" onClick={() => onOpen("economy", "dock")} data-testid="topbar-uxd" title={`Worth ${fmt(econ.worth)} UXD`}
            className={`${PANEL} lift pointer-events-auto relative rounded-[20px] px-4 py-1.5 text-center`}>
            <div ref={pulse} className="money flex items-center justify-center gap-1.5 text-xl font-semibold">
              <Coins className="h-5 w-5 text-x-gold" strokeWidth={2.2} /><Reel value={econ.uxd} />
            </div>
            <div className="text-[11px] font-medium tabular-nums text-x-pos">+{fmt(econ.rate)}/h</div>
          </button>
        ) : me?.status === "finished" && <Skel className="h-[52px] w-28" />}
      </div>

      <div className="relative flex shrink-0 items-center gap-1.5 sm:justify-self-end sm:gap-2">
        <Tray icon={Trophy} label="Leaderboard (L)" testId="topbar-leaderboard" quiet badge={rank && `#${rank}`} onClick={() => onOpen("leaders", "week")} />
        {econ && <Tray icon={Swords} label="Duels (D)" testId="topbar-duels" badge={econ.duels.inbox} dot={econ.duels.free && !econ.duels.inbox} onClick={() => onOpen("economy", "duels")} />}
        {econ && <Tray icon={ShoppingBag} label="Shop (S)" testId="topbar-shop" onClick={() => onOpen("shop")} className="max-sm:hidden" />}
        <Tray icon={MessageCircle} label="City chat (C)" testId="topbar-chat" badge={unread > 9 ? "9+" : unread} onClick={onChat} />
        <Tray icon={Newspaper} label="The Ledger" testId="topbar-ledger" dot={newPaper} onClick={onLedger} className="max-sm:hidden" />
        <Tray icon={Landmark} label="City Hall (H)" testId="topbar-hall" quiet badge={closes} onClick={() => onOpen("hall")} className="max-sm:hidden" />
        <Tray icon={Menu} label="Menu" testId="topbar-menu" data-pop onClick={toggle("menu")} />
        {menu === "menu" && (
          <Pop onClose={() => setMenu(null)} className="absolute right-0 top-full mt-2 w-64">
            <div className="flex items-center gap-2 px-3 py-2"><Mark size={22} /><span className="text-sm font-semibold">XField</span></div>
            {econ && <MenuItem icon={ShoppingBag} onClick={go(() => onOpen("shop"))} className="sm:hidden">Shop</MenuItem>}
            <MenuItem icon={Newspaper} onClick={go(onLedger)} className="sm:hidden">The Ledger{newPaper && <span className="ml-auto h-2 w-2 rounded-full bg-x-pos" />}</MenuItem>
            <MenuItem icon={Landmark} onClick={go(() => onOpen("hall"))} className="sm:hidden">City Hall{closes && <span className="ml-auto text-xs tabular-nums text-x-mute">{closes}</span>}</MenuItem>
            {econ && (
              <MenuItem icon={Compass} onClick={() => onGuide(!guide)} role="switch" aria-checked={guide} data-testid="menu-guide">
                Guide<span className={`ml-auto text-xs font-semibold ${guide ? "text-x-pos-ink" : "text-x-mute"}`}>{guide ? "On" : "Off"}</span>
              </MenuItem>
            )}
            {econ && <MenuItem icon={CircleHelp} onClick={go(onTour)} data-testid="menu-tour">Replay the tour</MenuItem>}
            {user ? signOut : <MenuItem icon={LogIn} onClick={go(onSignIn)} data-testid="menu-signin">Sign in with X</MenuItem>}
            <div className="mx-3 my-1 border-t border-x-line" />
            <ThemePicker />
          </Pop>
        )}
      </div>

      <div className="absolute top-full mt-2 -translate-x-1/2 transition-[left] duration-300"
        style={{ left: panel && !phone ? "max(158px, min(50%, calc(100% - 575px)))" : "50%" }}><SoundDeck compact={phone} /></div>
    </header>
  );
}
